import { useEffect, useState } from "react";
import { Outlet } from "react-router";
import PitchWatcher from "~/components/agencies/PitchWatcher";
import AppSidebar from "~/components/AppSidebar";
import AuthLoader from "~/components/AuthLoader";
import PastDueBanner from "~/components/billing/PastDueBanner";
import ViewerBanner from "~/components/ViewerBanner";
import NavigationHeader from "~/components/NavigationHeader";
import { SidebarInset, SidebarProvider } from "~/components/ui/sidebar";
import { Toaster } from "~/components/ui/sonner";
import { useAuth } from "~/context/AuthContext";
import { BreadcrumbProvider } from "~/context/BreadcrumbContext";
import { SubscriptionProvider } from "~/context/SubscriptionContext";
import { useScrollRestoration } from "~/hooks/useScrollRestoration";
import { useNavigate } from "~/lib/team-router";

interface Conversation {
  id: string;
  title: string;
  created_at: string;
}

export type AppLayoutContext = { headerActions: HTMLDivElement | null };

function AppLayoutContent() {
  const { user } = useAuth();
  const scrollContainerRef = useScrollRestoration<HTMLDivElement>("app");
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [headerActions, setHeaderActions] = useState<HTMLDivElement | null>(
    null
  );
  const [sidebarOpen] = useState(() => {
    const saved = localStorage.getItem("sidebar:state");
    return saved !== null ? saved === "true" : true;
  });

  useEffect(() => {
    localStorage.setItem("sidebar:state", sidebarOpen.toString());
  }, [sidebarOpen]);

  if (!user) {
    return null;
  }

  return (
    <SidebarProvider
      className="h-screen min-h-0 overflow-hidden"
      style={
        {
          "--sidebar-width": "calc(var(--spacing) * 72)",
          "--header-height": "calc(var(--spacing) * 12)",
        } as React.CSSProperties
      }
    >
      <AppSidebar
        user={user}
        sidebarOpen={sidebarOpen}
        conversations={conversations}
        setConversations={setConversations}
      />
      <SidebarInset className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex shrink-0 flex-wrap items-center">
          <div className="min-w-0 flex-1">
            <NavigationHeader conversations={conversations} />
          </div>
          <div
            ref={setHeaderActions}
            className="px-4 pb-2 empty:hidden md:pb-0 md:pl-0"
          />
        </div>
        <PastDueBanner />
        <ViewerBanner />
        <PitchWatcher />

        <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div
            ref={scrollContainerRef}
            className="@container/main flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto"
          >
            <Outlet context={{ headerActions } satisfies AppLayoutContext} />
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

export default function AppLayout() {
  const { isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return <AuthLoader />;
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <SubscriptionProvider>
      <BreadcrumbProvider>
        <Toaster richColors position="top-center" />
        <AppLayoutContent />
      </BreadcrumbProvider>
    </SubscriptionProvider>
  );
}
